/* ============================================================
   ROOMS V2 - THE POUR, the machinery. Contract: steering/15-the-pour.md.

   An ADDITIVE layer over the two guided rooms (the finder and the Duet).
   It observes; it never drives. Everything it knows it reads off seams
   the rooms already maintain for other reasons:

     data-at            which chapter the room is on (both controllers)
     data-chat-context  the live brief, kept for Jennie (D12) - the one
                        honest, bounded serialisation of what the
                        shopper has chosen
     .is-live/.is-flat  the honesty ladder

   What it does is TWO things, and the second is a reading of the first:

     THE TINT. The room slowly takes the muted hue of the family being
     chosen (the ground, the crests, the chip rims, the seam), and every
     option carries its own family's hue so its crest and rim arrive
     already tinted.

     THE FIELD (2026-08-31, owner: "add some more cool animations, perhaps
     some canvas animations ... shifting colors and stuff vibes very
     complex ... Depending what you select the vibes and colors may change
     dynamically"). One <canvas> per room paints the ground itself: six
     slow bodies of tinted liquid wandering a field that never repeats,
     re-coloured by every answer. THE VIBE FIELD below carries the whole
     cost argument - it is the cheapest layer in either room.

   THE VIAL AND THE DROPLETS ARE GONE (owner, 2026-08-27: "Don't like
   the liquid bottle at the top left"). The corner vessel, the flying
   drops and the slosh were removed outright - the server-rendered
   trail ticks are the progress again, exactly as v1 drew them. Do not
   rebuild them here without a fresh ask.

   If this file never loads, or the room flattens, v1 stands whole
   underneath - nothing here is load-bearing. No engine, no fetch, no
   state the room does not already hold.
   ============================================================ */
(() => {
  'use strict';

  const root = document.querySelector('[data-finder], [data-duet]');
  if (!root) return;
  const isFinder = root.hasAttribute('data-finder');

  /* ---- the tint vocabulary. The same muted family hues the nebula
     paints its glass with (theme.js FAM_TINT) - desaturated and close in
     value on purpose, so the room leans, never shouts. ---- */
  const TINT = {
    citrus: 'hsl(52 46% 58%)', fresh: 'hsl(180 20% 60%)', aquatic: 'hsl(204 32% 58%)',
    green: 'hsl(110 18% 50%)', aromatic: 'hsl(140 16% 48%)', fruity: 'hsl(8 40% 60%)',
    floral: 'hsl(335 30% 64%)', powdery: 'hsl(280 14% 66%)', musky: 'hsl(30 12% 56%)',
    spicy: 'hsl(20 45% 48%)', woody: 'hsl(28 32% 44%)', gourmand: 'hsl(35 48% 54%)',
    resinous: 'hsl(15 35% 42%)', leather: 'hsl(20 25% 36%)', amber: 'hsl(38 52% 50%)'
  };
  /* the catalogue's own category words, folded onto that vocabulary */
  const ALIAS = {
    ambery: 'amber', amber: 'amber', musk: 'musky', sweet: 'gourmand',
    herbal: 'aromatic', marine: 'aquatic', earthy: 'woody', woods: 'woody',
    balsamic: 'resinous', smoky: 'leather', oriental: 'amber', tropical: 'fruity'
  };
  const SEASON_TINT = {
    summer: 'hsl(50 34% 58%)', winter: 'hsl(210 16% 56%)',
    autumn: TINT.amber, spring: TINT.green
  };
  const VIBE_TINT = {
    one: '', loud: TINT.spicy, day: 'hsl(48 26% 58%)', night: 'hsl(226 14% 46%)'
  };
  const NEUTRAL = 'hsl(32 14% 52%)';

  function famTint(word) {
    if (!word) return '';
    const key = String(word).toLowerCase().split('|')[0].trim();
    return TINT[key] || TINT[ALIAS[key]] || '';
  }

  root.classList.add('lq-live');

  /* ---- the state feed. data-chat-context is already the bounded,
     honest serialisation of every choice (it exists for Jennie); the
     tint is a reading of it, never a second store. ---- */
  function readCtx() {
    try { return JSON.parse(root.dataset.chatContext || 'null'); }
    catch (err) { return null; }
  }
  function deckFam(handle) {
    if (!handle || isFinder) return '';
    let tile = null;
    try { tile = root.querySelector('[data-dt-pick="' + (window.CSS && CSS.escape ? CSS.escape(handle) : handle) + '"]'); }
    catch (err) { tile = null; }
    return tile ? famTint(tile.dataset.fam) : '';
  }

  function applyState() {
    if (root.classList.contains('is-flat')) { teardown(); return; }
    const ctx = readCtx();
    let tint = '', tintB = '';
    if (isFinder) {
      if (ctx) {
        const se = ctx.season ? (SEASON_TINT[String(ctx.season).toLowerCase()] || '') : '';
        /* ---- A NOTE OUTRANKS A FAMILY (2026-08-31, owner: "slight color
           shifts depending on the notes. vibes and scents etc... each note
           has like a color vibe tho and or category").

           "Floral" is a shelf; "Tuberose" is a decision. When a shopper has
           gone into the notes and loved something, that is the most specific
           thing they have told this room, so it takes the first colour and
           the character question takes the second. Two different loved
           families give two liquids; one gives one, and the character or the
           season fills the other side.

           The families come off the note rows themselves (theme.js stamps
           data-fam from the nebula's own byNote map), not off the brief: the
           brief carries the loved notes as LABELS for Jennie to read aloud,
           and matching a label back to a family here would be a second, worse
           copy of a map that already exists. ---- */
        const lovedFams = [];
        root.querySelectorAll('[data-fx-note].is-loved').forEach((row) => {
          const f = row.dataset.fam;
          if (f && lovedFams.indexOf(f) === -1) lovedFams.push(f);
        });
        tint = famTint(lovedFams[0]) || famTint(ctx.character && ctx.character[0]);
        /* TWO LIQUIDS WHENEVER THERE ARE TWO ANSWERS (2026-08-31). A second
           character takes the second colour, and failing that the season
           does, so "Florals, winter" is a field with two hues folding
           through each other rather than one flat lean. The finder used to
           take the season ONLY when no character had been named, which meant
           the two questions could never both be on screen at once. */
        tintB = famTint(lovedFams[1])
          || famTint(ctx.character && (lovedFams.length ? ctx.character[0] : ctx.character[1]))
          || se;
        if (!tint) tint = se;
      }
    } else if (ctx) {
      tint = deckFam(ctx.anchor_handle) || famTint(ctx.character && ctx.character[0]);
      tintB = deckFam(ctx.partner_handle);
      if (!tint && ctx.vibe) {
        const vk = String(ctx.vibe).toLowerCase();
        if (vk.indexOf('daylight') !== -1) tint = VIBE_TINT.day;
        else if (vk.indexOf('dark') !== -1) tint = VIBE_TINT.night;
      }
    }
    root.style.setProperty('--lq-tint', tint || NEUTRAL);
    root.style.setProperty('--lq-tint-b', tintB || tint || NEUTRAL);
    /* the field takes the same two colours the sheet just took. It is handed
       the hsl() LITERALS this file already holds rather than reading them back
       off the computed style: --lq-tint is a registered property in the middle
       of a 1.1s transition, so a read would sample the tween and the canvas
       would spend the rest of the session chasing its own tail. */
    field.colours(tint || NEUTRAL, tintB || tint || NEUTRAL);
    /* and the SHAPE, from the same reading (moodOf: presence, notes, the
       clock). Colour and shape are one reading of one brief, which is why
       they are set on the same line. */
    field.mood(moodOf(ctx));
  }

  /* ---- each option remembers its own family, so its crest, its rim
     and its underline arrive already tinted. Painted per chapter because
     the Duet's quiz panels are script-built when they are walked into. ---- */
  function paintOptionTints() {
    /* only what changed is written: this runs on every chapter, and an
       inline write of the same value still dirties the node's style */
    const put = (node, t) => { if (t && node.style.getPropertyValue('--lq-t') !== t) node.style.setProperty('--lq-t', t); };
    if (isFinder) {
      root.querySelectorAll('[data-fx-pick="character"]').forEach((n) => put(n, famTint(n.dataset.fxValue)));
      root.querySelectorAll('[data-fx-pick="season"]').forEach((n) => put(n, SEASON_TINT[String(n.dataset.fxValue || '').toLowerCase()] || ''));
    } else {
      root.querySelectorAll('[data-dt-qpick="character"]').forEach((n) => put(n, famTint(n.dataset.dtQval)));
      root.querySelectorAll('[data-dt-qpick="season"]').forEach((n) => put(n, SEASON_TINT[String(n.dataset.dtQval || '').toLowerCase()] || ''));
      root.querySelectorAll('[data-dt-fam]').forEach((n) => put(n, famTint(n.dataset.dtFam)));
      root.querySelectorAll('[data-dt-vibe]').forEach((n) => put(n, VIBE_TINT[n.dataset.dtVibe] || ''));
    }
  }

  let dead = false;

  /* ============================================================
     THE VIBE FIELD - the ground, painted rather than declared.

     WHAT IT IS. Six soft bodies of liquid wandering one field: four
     carrying the two family tints the room is holding, two carrying the
     warm shade every JUCE shadow is mixed from. They ride Lissajous
     paths whose frequency ratios are incommensurable, so the composition
     never returns to itself, and they are re-COLOURED rather than
     re-drawn whenever an answer moves the tint. That is the whole of
     "the vibes and colors change dynamically depending what you select".

     WHY A CANVAS AT ALL, given steering/12. Because it is CHEAPER than
     what it joins, not more expensive, and the numbers are the argument:

       - the backing store is 190px on its long side. Six radial
         gradients over ~35,000 pixels is a fraction of the work one
         full-screen CSS gradient repaint does, and the room was already
         doing that for the two pseudo-lenses underneath.
       - the CSS scales that 190px buffer up to the room. THE UPSCALE IS
         THE BLUR - the compositor's own bilinear filter, free, on the
         GPU - which is how this gets a liquid look with no `filter` and
         no `backdrop-filter` anywhere near it. That is the one rule
         steering/12 exists to protect and this never touches it.
       - 20fps, not 60. Nothing here moves faster than a minute a lap; a
         third of the frames is indistinguishable to the eye and two
         thirds of the work simply never happens.
       - it stops dead when it cannot be seen: a hidden tab, a flattened
         room, reduced motion (one frame, then still). No timer survives
         teardown.

     THE COLOUR LAW IS THE SHEET'S, ENFORCED IN CODE. Every body is
     painted as an ALPHA over the ground and every colour is mixed 45%
     into the room's own warm shade before it is used, so no pixel this
     file writes can be LIGHTER than var(--ground). That is not taste:
     the packshots melt on `mix-blend-mode: darken` and one lighter pixel
     under a bottle prints its #F2F2F2 bed back as a rectangle (duet.css,
     THE LIGHT FIELD). It is also why "the tint is a hue lean, never a
     lightness lift" finally has somewhere it is actually enforced.

     WHERE IT LIVES. Exactly where the pseudo-lenses live - inside the
     panel that is on, at z-index -1 - because that is the only side of
     `.sf-panel` / `.dt-panel`'s stacking context a packshot can blend
     with. There is ONE canvas per room and it MOVES to whichever panel
     is showing, so a five-chapter walk still only ever composites one.
     It is inserted as the panel's FIRST child and is not an ancestor of
     anything, so L3 is untouched.
     ============================================================ */
  /* ---- RE-TUNED 2026-08-31, same day it shipped (owner: "also the canvas
     cutt of weird ... the image on left is too bright").

     BOTH OF THOSE WERE ONE NUMBER TOO BIG. Measured on t/26 at 1440 and at
     1900, the ground had gone from the 233-242 it has always been to
     207-224 - thirteen per cent darker than #F2F2F2, when the standing law
     (steering/15 §4) is "within a few percent". Six bodies at up to 0.085
     alpha, each painted over the WHOLE canvas, stack: four overlapping at a
     point is a third of the way to the shade.

     Two consequences, and the owner named both. The room went flat and
     murky, so the answer's photograph - which is a pale bottle on a #F2F2F2
     bed - read as a bright patch in a grey room rather than a bottle
     standing on the ground. And the bodies were HUGE (r up to 0.62 of the
     long side, so 1.2 screens across): at that scale the field is not an
     atmosphere, it is one half of the window being lighter than the other,
     with a soft vertical boundary between them. That is the "cut off".

     So: the alphas are cut by about EIGHT (five was still 2.5% of the
     ground's own value away and the picture still read as the lighter thing
     in the room), and the bodies are half the size and there are nine of
     them. Nine small bodies at a twentieth of the
     ground's own contrast marble; six huge ones at a tenth divide the room
     in two. Measured after: 235-242 at every width, which is where the
     packshots melt and where this file's own colour law says to be. ---- */
  /* ============================================================
     THE LIQUID FIELD (rebuilt 2026-08-31, owner: "we need some cool
     movement, canvas things animations and things like that liquid like
     effect and changes depending on layout, style, notes and stuff like
     that etc... etc...").

     WHAT IT WAS. Nine radial gradients wandering a canvas. Even with the
     speed fixed that is a lava lamp: soft round bodies sliding over one
     another, no structure, and the only thing it could ever say about the
     shopper was its colour. It could not look like liquid because it had
     no EDGES - a liquid reads as liquid where two bodies fold into each
     other, and a stack of radial gradients has no folds in it.

     WHAT IT IS NOW. One per-pixel field, domain-warped. Two slow wave
     fields displace the coordinates that a third set of waves is read at,
     and what is drawn is the RIDGE of that third set (1 - |n|, then
     smoothstepped) rather than the waves themselves. The ridge is the
     fold. It marbles, it never repeats - every frequency below is
     irrational against the others - and it moves like something poured
     rather than something floating.

     WHAT IT COSTS, because "optimised and not laggy" is the standing
     instruction. The backing store is 168px on its long side and CSS
     scales it to the room, so a 1440x950 window is 168 x 111 = 18,648
     pixels a frame at 20fps, 373k pixels a second. Every sine is a lookup
     in a 2048-entry table indexed by a masked integer, so Math.sin never
     runs inside the loop; the buffer is allocated once per size and
     written in place. And it WATCHES ITSELF - see THE STEP DOWN.

     WHAT IT READS. Colour has come from the family being chosen since the
     day it shipped. Now the SHAPE comes from the rest of the brief:

       presence   the size of the veins. "Close to the skin" is fine and
                  quiet, "Bold" is large and open. This is the dial you
                  feel first.
       liked      how twisted the warp is. Notes carry no hue of their own
                  in this file - the finder's note rows carry a slug and a
                  name, not a family - so what they buy is INTRICACY: six
                  notes picked gives a far more involved field than none.
                  Honest about what it knows, and visible.
       when/vibe  the clock. "Loud" runs half again as fast, "After dark"
                  slower.
       season     the SECOND colour once the character has taken the
                  first, so "Florals, winter" is two liquids and not one.

     AND THE LAYOUT, which is the dial that keeps the colour law true. A
     panel carrying a PHOTOGRAPH gets the field at 45 per cent: the
     answer's packshot stands on a #F2F2F2 bed, and a ground that swings
     under a bed that does not is exactly how you get "the image on left
     is too bright" (owner, of the version that swung 35 levels). Panels
     that are type and controls get it whole. Measured at full strength:
     alpha spans 2 to 16 of 255, holding the ground between 228 and 242,
     which is under six per cent off #F2F2F2 and nowhere near the 207-224
     that was rejected.
     ============================================================ */
  /* ---- CALMER, AND CHEAPER WITH IT (2026-09-01, owner: "the effects is
     too much make it more optimised cleaner and sophisticated").

     Nothing here is removed; four numbers come down. The filaments were the
     loud part - thin, high-contrast and read at three times the body's rate,
     which is a lot of edges in a small space and reads as busy rather than
     as water. They are broader and quieter now: two thirds the rate, five
     alpha instead of eight. The fold behind them comes down with them, the
     ring is a suggestion rather than a splash, and the depth ramp halves.

     And the backing store drops from 168 to 144 on its long side, which is
     a quarter fewer pixels a frame for a picture that is upscaled by CSS
     anyway - 13,700 rather than 18,600 at 1440x950. "Optimised" was asked
     for literally, so it is answered literally as well as visually. ---- */
  const FIELD_LONG = 144;         /* long side of the backing store, px */
  const FIELD_MS = 50;            /* 20fps - the field is slower than the eye */
  /* ---- WHY THE COLOUR WAS NOT ARRIVING (2026-08-31, owner: "There is no
     color bg that is cahnging anf stuff and mixing as you select new
     catrgories and notes and stuff like that, background liquids").

     The tints were changing. Measured, loving Amber walked --lq-tint to
     rgb(192,145,63) exactly as it should. Almost none of it reached the eye,
     and the reason was this file's own paint: every pixel was mixed 55/45
     into the warm ink and then composited at about a tenth of alpha. That is
     two multiplications by a small number applied to the same thing. A
     family hue at 45% ink is already a grey; at 10% over the ground it is a
     grey you cannot name.

     It was fighting itself, too. The mix into the ink existed to hold the
     value law - "hue moves, value does not" - but mixing toward BLACK is a
     value move by definition, so the only way to keep the room light was to
     drop the alpha, and the only way to make the colour visible was to raise
     it. There was no setting that gave both.

     SO THE PAINT IS SPLIT IN TWO. Colour and structure are separate
     contributions to the same pixel now:

       THE COLOUR is the family hue at NINETY PERCENT LIGHTNESS - a pale,
       clearly-hued wash whose luminance is within a few points of the
       ground's own. Because it is the same value as what is under it, it can
       be laid on at four times the old alpha and shift the hue plainly while
       moving the value by two or three levels. That is the law being KEPT,
       not bent: hue moves a lot, value hardly at all.

       THE STRUCTURE is the folds and the filaments, and only they carry the
       warm ink. They are thin and they are what gives the water its depth.

     Each pixel's final colour is the two weighted by their own alphas, so a
     vein is dark and tinted and the water between the veins is pale and
     tinted, and the room reads as the colour of whatever was last chosen. ---- */
  const COLOUR_A = 64;            /* the wash, of 255. Pale, so it may be loud */
  const COLOUR_L = 0.90;          /* the wash's lightness. Near the ground's */
  const COLOUR_S = 1.35;          /* x the family's own saturation: a pale hue
                                     needs more of it to still read as a hue */
  const FIELD_ALPHA = 9;          /* the fold, of 255 */
  const FIELD_FLOOR = 2;          /* the palest. Never 0: a field that reaches
                                     nothing has a visible edge where it stops */
  const SHOT_AMP = 0.45;          /* panels that carry a photograph */
  const SHADE = [27, 24, 20];     /* the warm ink every shadow here is mixed from */

  /* THE TABLE. 2048 entries, indexed in TURNS - one whole cycle is x to
     x + 1 - so the index is (x * 2048) | 0 masked to the table, and a
     negative x wraps correctly because a bitwise AND reads the integer as
     two's complement. Built once, at parse. */
  const LUT = 2048, LMASK = LUT - 1;
  const SIN = new Float32Array(LUT);
  for (let li = 0; li < LUT; li += 1) SIN[li] = Math.sin(li * 2 * Math.PI / LUT);
  function sn(x) { return SIN[((x * LUT) | 0) & LMASK]; }

  function hslToRgb(str) {
    const m = /hsl\(\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*\)/.exec(String(str || ''));
    if (!m) return [140, 132, 120];
    const h = ((+m[1] % 360) + 360) % 360 / 360;
    const sat = +m[2] / 100;
    const li = +m[3] / 100;
    if (!sat) { const v = Math.round(li * 255); return [v, v, v]; }
    const q = li < 0.5 ? li * (1 + sat) : li + sat - li * sat;
    const pp = 2 * li - q;
    const hue = (x0) => {
      let x = x0;
      if (x < 0) x += 1;
      if (x > 1) x -= 1;
      if (x < 1 / 6) return pp + (q - pp) * 6 * x;
      if (x < 1 / 2) return q;
      if (x < 2 / 3) return pp + (q - pp) * (2 / 3 - x) * 6;
      return pp;
    };
    return [hue(h + 1 / 3), hue(h), hue(h - 1 / 3)].map((v) => Math.round(v * 255));
  }

  /* the same parse, re-lit. The vocabulary above is written at the lightness
     a SWATCH wants (58%ish); what the wash wants is the same hue at the
     ground's own value, with the saturation pushed because a pale colour
     loses its name faster than a mid one. */
  function hslToWash(str) {
    const m = /hsl\(\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*\)/.exec(String(str || ''));
    if (!m) return hslToRgb('hsl(32 10% ' + Math.round(COLOUR_L * 100) + '%)');
    const sat = Math.min(96, +m[2] * COLOUR_S);
    return hslToRgb('hsl(' + m[1] + ' ' + sat.toFixed(1) + '% ' + (COLOUR_L * 100).toFixed(1) + '%)');
  }

  const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  const isStill = () => !!(still && still.matches);

  /* ---- WHAT THE BRIEF DOES TO THE LIQUID. One table, read on every
     change, and every value is walked toward rather than snapped: the room
     LEANS into an answer the same way its colour does. ---- */
  const PRESENCE = {
    close: { scale: 1.62, amp: 0.74 },
    balanced: { scale: 1.00, amp: 1.00 },
    bold: { scale: 0.66, amp: 1.26 }
  };
  function moodOf(ctx) {
    const m = { scale: 1, warp: 1, speed: 1, amp: 1 };
    if (!ctx) return m;
    const p = PRESENCE[String(ctx.presence || '').toLowerCase()];
    if (p) { m.scale = p.scale; m.amp = p.amp; }
    /* the notes are intricacy, not colour */
    const liked = (ctx.liked && ctx.liked.length) || 0;
    m.warp = 0.72 + Math.min(6, liked) * 0.13;
    const when = String(ctx.when || '').toLowerCase();
    if (when.indexOf('night') !== -1 || when.indexOf('even') !== -1) m.speed *= 0.72;
    else if (when.indexOf('day') !== -1 || when.indexOf('morning') !== -1) m.speed *= 1.12;
    const vibe = String(ctx.vibe || '').toLowerCase();
    if (vibe.indexOf('loud') !== -1) m.speed *= 1.5;
    else if (vibe.indexOf('dark') !== -1) m.speed *= 0.72;
    else if (vibe.indexOf('one') !== -1) m.speed *= 0.85;
    return m;
  }

  /* ---- WATER, NOT WEATHER (2026-08-31, owner: "Where is like the liquid
     like effects and things? and movements and stuff? when I say liquid I
     mean like water and stuff and not liquid code").

     The field before this was a marbled ground: correct, measured, and
     abstract. It looked like stone or like smoke. Three things below make it
     read as a surface of water instead, and only the first is new maths.

     THE DROP. Every tap in the room puts a ring on the water. It is a real
     travelling wave rather than a fading circle - the crest moves outward at
     RIP_C and the shape carried through it is one full oscillation, a trough
     then a crest, so it passes THROUGH the picture rather than blooming in
     place. Four can be alive at once and each lasts under three seconds.

     AND THE RING BENDS WHAT IS UNDER IT. The same value is added to the warp
     coordinates before the veins are read, not only to the alpha afterwards,
     so the standing pattern is displaced as the ring crosses it. That is the
     whole difference between a ripple drawn ON water and a ripple drawn OVER
     a picture: on real water the thing you see move is the reflection.

     THE CAUSTIC. A second, much sharper ridge read at the same warped
     coordinate and raised to the fourth power, which turns a broad band into
     a thin filament. Broad bodies alone read as fog; the filaments are what
     the eye calls water, because they are what light does on a moving
     surface. They are thin, so they cost almost nothing in average
     luminance even though they are the most visible thing here.

     WHAT IT COSTS. The ripples are the only new per-pixel work and they are
     bounded: a ring is skipped for any pixel more than 2.6 ring-widths from
     its crest, which is most of the field most of the time, and a ring that
     has lived its life is spliced out. While any ring is alive the field
     runs at 35fps instead of 20 so the wave does not step; the moment they
     are gone it drops back. Idle cost is unchanged. ---- */
  const RIP_MAX = 4;              /* rings alive at once */
  const RIP_LIFE = 2.8;           /* seconds */
  const RIP_C = 0.40;             /* crest speed, field widths per second */
  const RIP_W = 0.085;            /* ring width, field widths */
  const RIP_AMP = 8;              /* crest, of 255. "just suttble mobbement" */
  const RIP_BEND_X = 1.9;         /* the ring leans on the WARP more than on
                                     the alpha now: displacing what is there
                                     reads as movement without drawing a
                                     shape of its own, which is the whole
                                     difference between a nudge and a splash */
  const RIP_BEND = 0.013;         /* how far the ring drags the pattern */
  /* THE FILAMENT CARRIES THE LOOK, so it gets the room it needs. 11 rather
     than 7 after two rounds of "where is it". It is safe to spend here in a
     way the broad body is not: the thing that was rejected at 207-224 was a
     LOW-frequency field, half the window darker than the other half with a
     soft boundary down the middle. Filaments are the opposite - thin, high
     frequency, everywhere at once - so they raise the mean by about two
     levels of 255 while being the most visible thing on the ground.
     Measured after: mean alpha 15, which is a mean ground of 229.

     PULLED BACK TO 8 (owner: "we can make the lines slighty more suttble but
     add mor edpenth to the effect system"). The filaments were carrying the
     whole look on their own, which is why they had to be loud. They do not
     any more: the depth ramp and the chromatic split below give the field
     dimension that does not come out of contrast, so the lines can go quiet
     and the picture gets richer rather than fainter. */
  /* ---- THE FILAMENTS ARE GONE (2026-09-01, owner: "Tone the bg cavans
     down to not have the lines and when you click, just suttble mobbement").

     They were the thing that made it read as water and they were also the
     thing that made it read as LINES, and after two rounds of turning them
     down the honest conclusion is that on a near-white ground there is no
     setting where a sharp ridge is both visible and quiet. Anything with an
     edge on this ground draws attention to the edge.

     So the ridge maths comes out entirely - not zeroed, removed, which takes
     two sines and two multiplies per pixel with it. What is left is the
     broad fold: soft, wide bodies of colour with no boundary anywhere in
     them, which is what "toned down" actually looks like. The chromatic
     split survives on the fold alone, so the water is still a different hue
     where it is deep. ---- */
  const FIELD_MS_LIVE = 28;       /* ~35fps while a ring is on the water */

  function makeField() {
    let cv = null, g = null, img = null, px8 = null;
    let raf = 0, last = 0, prev = 0, w = 0, h = 0, host = null;
    let long = FIELD_LONG, slow = 0, phase = 0, clock = 0;
    /* the live colour and the one we are heading for */
    const now = [hslToWash(NEUTRAL), hslToWash(NEUTRAL)];
    const want = [hslToWash(NEUTRAL), hslToWash(NEUTRAL)];
    const dial = { scale: 1, warp: 1, speed: 1, amp: 1 };
    const aim = { scale: 1, warp: 1, speed: 1, amp: 1 };
    let ampAim = 1;                  /* the layout's own multiplier */
    let pxA = 0.5, pyA = 0.5, pxN = 0.5, pyN = 0.5;
    const rings = [];

    /* THE HOST'S BOX IS READ ONCE, NOT ONCE A FRAME. getBoundingClientRect
       forces layout, and size() ran inside paint() on every painted frame -
       about 35 layout reads a second while a ring was on the water, for a
       box that only changes on a resize, a visibility change or a new host.
       Each of those drops the cache (rect = null); the next size() reads
       it fresh. */
    let rect = null, sizeRaf = 0;
    function forget() { rect = null; }
    /* THE HOST TELLS US ITS SIZE (2026-09-22, lane 8). steering/12 §6.1: no
       raw resize listeners, and this was one - plus a getBoundingClientRect
       on the first paint after every step, which forced a layout in the
       middle of the panel slide. A ResizeObserver on the panel the canvas
       lives in hands over the border box without a forced read, fires only
       when that box really changes (a phone's address bar showing and hiding
       does not resize an inset:0 panel), and is re-pointed when the canvas
       moves. Browsers without it keep the old coalesced listener. */
    const ro = window.ResizeObserver ? new window.ResizeObserver((ents) => {
      const e = ents[ents.length - 1];
      const bs = e && e.borderBoxSize && e.borderBoxSize[0];
      rect = bs ? { width: bs.inlineSize, height: bs.blockSize } : null;
      /* under reduced motion nothing is ticking, so the still frame is
         re-drawn at the new size here rather than left stretched */
      if (host && cv && isStill()) frame();
    }) : null;
    if (!ro) {
      window.addEventListener('resize', () => {
        if (sizeRaf) return;
        sizeRaf = window.requestAnimationFrame(() => {
          sizeRaf = 0;
          forget();
          if (host && cv && isStill()) frame();
        });
      }, { passive: true });
    }
    /* THE HOLD (2026-09-22, lane 8). A step slides one panel out and the next
       in over about 600ms, on the compositor, and the field used to paint
       straight through it at 35fps with up to three rings on the water (the
       press, the colour change and the chapter). Measured on a 4x throttled
       phone that was the canvas competing with the slide for every frame.
       So a step HOLDS the field: the canvas moves into the new panel at once,
       carrying the frame it last painted, rides the slide as a still layer
       and starts moving again the moment the panel lands. A ring asked for
       during the hold is poured when it ends, which is also the better
       moment for "the room pours into the chapter it just walked into". */
    let holdT = 0;
    const held = [];
    function size() {
      if (!host || !cv) return false;
      const b = rect || (rect = host.getBoundingClientRect());
      if (b.width < 2 || b.height < 2) { rect = null; return false; }
      const l = Math.max(b.width, b.height);
      const nw = Math.max(16, Math.round(long * (b.width / l)));
      const nh = Math.max(16, Math.round(long * (b.height / l)));
      if (nw === w && nh === h && px8) return true;
      w = nw; h = nh; cv.width = w; cv.height = h;
      img = g.createImageData(w, h);
      px8 = img.data;
      return true;
    }

    function paint(ms) {
      if (!g || !size()) return;
      const t0 = (window.performance && performance.now()) || 0;
      const dt = prev ? Math.min(0.25, (ms - prev) / 1000) : 0.05;
      prev = ms;
      /* SPEED INTEGRATES INTO A PHASE rather than multiplying the clock.
         Multiplying it would make every answer that changes the speed jump
         the whole composition to a different place in its own future. The
         rings run on `clock`, which is real seconds: a wave that sped up
         because somebody chose "Loud" would not be a wave. */
      phase += dt * dial.speed;
      clock += dt;
      dial.scale += (aim.scale - dial.scale) * 0.045;
      dial.warp += (aim.warp - dial.warp) * 0.045;
      dial.speed += (aim.speed - dial.speed) * 0.045;
      dial.amp += (aim.amp * ampAim - dial.amp) * 0.045;
      pxA += (pxN - pxA) * 0.035;
      pyA += (pyN - pyA) * 0.035;
      for (let i = 0; i < 2; i += 1) {
        for (let c = 0; c < 3; c += 1) now[i][c] += (want[i][c] - now[i][c]) * 0.045;
      }

      /* the rings, advanced once a frame rather than once a pixel */
      for (let k = rings.length - 1; k >= 0; k -= 1) {
        const age = clock - rings[k].t0;
        if (age > RIP_LIFE) { rings.splice(k, 1); continue; }
        rings[k].front = age * RIP_C;
        const f = 1 - age / RIP_LIFE;
        rings[k].fade = f * f;
      }
      const nRing = rings.length;

      const t = phase;
      const sc = dial.scale;
      const wp = dial.warp * 0.22;
      const ox = (pxA - 0.5) * 0.10;
      const oy = (pyA - 0.5) * 0.10;
      const a0 = now[0], a1 = now[1];
      const sr = SHADE[0], sg = SHADE[1], sb = SHADE[2];
      const floor = FIELD_FLOOR, span = FIELD_ALPHA - FIELD_FLOOR;
      const amp = dial.amp;
      const ar = h / w;                  /* keeps a ring round, not oval */
      let i = 0;
      for (let y = 0; y < h; y += 1) {
        const v = y / h;
        for (let x = 0; x < w; x += 1) {
          const u = x / w;

          /* ---- the rings ---- */
          let rip = 0;
          for (let k = 0; k < nRing; k += 1) {
            const rg = rings[k];
            const dx = u - rg.x;
            const dy = (v - rg.y) * ar;
            const q = (Math.sqrt(dx * dx + dy * dy) - rg.front) / RIP_W;
            if (q > -2.6 && q < 2.6) {
              const env = 1 - q * q * 0.148;
              rip += sn(q * 0.19) * env * rg.fade;
            }
          }

          /* the two warp fields, dragged by whatever ring is passing */
          const w1 = sn(v * 0.62 * sc + t * 0.031) + sn(u * 0.48 * sc - t * 0.024);
          const w2 = sn(u * 0.71 * sc - t * 0.019) + sn(v * 0.55 * sc + t * 0.027);
          const uu = u + wp * w1 + ox + rip * RIP_BEND * RIP_BEND_X;
          const vv = v + wp * w2 + oy + rip * RIP_BEND * RIP_BEND_X;

          /* the body of the liquid */
          let n = sn(uu * 1.13 * sc + t * 0.017)
                + sn(vv * 0.94 * sc - t * 0.013)
                + sn((uu + vv) * 0.71 * sc + t * 0.0091);
          n *= 0.33333;
          /* THE FOLD. 1 - |n| puts a ridge wherever the waves cross zero,
             and the smoothstep gives that ridge a shoulder instead of a
             crease, which is the difference between marble and a contour
             map. */
          let f = 1 - (n < 0 ? -n : n);
          f = f * f * (3 - 2 * f);

          /* ---- DEPTH. Two things, and neither is contrast.

             THE RAMP: water is denser the further down you look into it, so
             the field carries a little more weight at the foot of the panel
             than at its head. It is a sixth either side of neutral, which is
             under three levels of ground across a whole screen - far too
             little to read as a gradient and exactly enough to stop the
             field reading as a flat sheet of pattern.

             THE SPLIT: the crests take one of the two liquids and the
             troughs lean toward the other. Before this the colour mix was a
             function of the warp alone, so a filament and the body it sat in
             were always the same hue and the picture was one colour with
             texture on it. Tying a fifth of the mix to the fold itself means
             the light on the surface is a different colour from the water
             under it, which is the whole of what makes water look deep. ---- */
          const depth = 0.90 + 0.18 * v;
          /* THE TWO CONTRIBUTIONS. `dk` is the warm ink the folds and the
             filaments are made of; `cl` is the pale hue of the water itself.
             They are separate because they answer to different laws - the
             ink is bounded by the value law and the wash is not, because at
             ninety percent lightness the wash barely has a value to move. */
          const dk = (floor + f * span) * depth * amp + (rip > 0 ? rip * RIP_AMP : 0);
          const cl = COLOUR_A * amp + (rip < 0 ? -rip * RIP_AMP : 0);
          let a = dk + cl;
          if (a < 1) { px8[i + 3] = 0; i += 4; continue; }
          if (a > 255) a = 255;

          /* which of the two liquids this pixel belongs to. It reads the
             fold as well as the warp, so the light ON the surface is a
             different hue from the water under it - which is the whole of
             what makes water look deep. */
          let mm = 0.5 + 0.25 * w1 + 0.22 * (f - 0.5);
          if (mm < 0) mm = 0; else if (mm > 1) mm = 1;
          const br = a0[0] + (a1[0] - a0[0]) * mm;
          const bg = a0[1] + (a1[1] - a0[1]) * mm;
          const bb = a0[2] + (a1[2] - a0[2]) * mm;
          /* the pixel's colour is the two weighted by their own alphas, so a
             vein is dark AND tinted and the water between the veins is pale
             AND tinted. One divide; there is no cheaper honest way to
             composite two coloured contributions into one RGBA. */
          const inv = 1 / a;
          px8[i] = (br * cl + sr * dk) * inv;
          px8[i + 1] = (bg * cl + sg * dk) * inv;
          px8[i + 2] = (bb * cl + sb * dk) * inv;
          px8[i + 3] = a;
          i += 4;
        }
      }
      g.putImageData(img, 0, 0);

      /* ---- THE STEP DOWN. A phone that cannot hold the frame is not told
         to try harder: three overruns and the backing store drops a quarter
         and keeps dropping until it either fits or reaches 96px, which is
         still a legible liquid because the whole thing is upscaled anyway.
         It never steps back UP - a field that breathes in and out of
         resolution would be worse than either. ---- */
      const cost = ((window.performance && performance.now()) || 0) - t0;
      if (cost > 12 && long > 96) {
        slow += 1;
        if (slow >= 3) { slow = 0; long = Math.round(long * 0.75); w = 0; h = 0; }
      } else if (cost < 8) slow = 0;
    }

    function tick(ms) {
      raf = 0;
      if (dead || !host) return;
      const step = rings.length ? FIELD_MS_LIVE : FIELD_MS;
      if (ms - last >= step) { last = ms; paint(ms); }
      raf = window.requestAnimationFrame(tick);
    }
    function run() {
      if (dead || raf || holdT || !host || document.hidden || isStill()) return;
      raf = window.requestAnimationFrame(tick);
    }
    function stop() { if (raf) { window.cancelAnimationFrame(raf); raf = 0; } }
    /* the still frame is a real frame of the composition, not the flat pose
       at t = 0 where every wave sits on its own zero */
    function frame() { phase = 12; prev = 0; rings.length = 0; paint(0); }

    return {
      to(panel) {
        if (dead || !panel) return;
        if (!cv) {
          cv = document.createElement('canvas');
          cv.className = 'lq-field';
          cv.setAttribute('aria-hidden', 'true');
          try { g = cv.getContext('2d'); } catch (err) { g = null; }
          if (!g) { cv = null; return; }
        }
        /* THE LAYOUT DIAL. A panel with a photograph on it takes the field
           at SHOT_AMP; everything else takes it whole. */
        ampAim = (panel.classList.contains('sf-panel--out')
          || panel.classList.contains('dt-panel--duet')) ? SHOT_AMP : 1;
        /* the same panel again: nothing to move, but reduced motion may
           have been switched ON since the loop started - run() alone would
           bail on the live raf and leave the field animating, so the loop
           is stopped here and one still frame drawn in its place */
        if (cv.parentElement === panel) {
          if (isStill()) { stop(); frame(); } else run();
          return;
        }
        host = panel;
        panel.insertBefore(cv, panel.firstChild);
        w = 0; h = 0; forget();
        if (ro) { ro.disconnect(); ro.observe(panel); }
        rings.length = 0;
        if (isStill()) { stop(); frame(); return; }
        run();
      },
      colours(a, b) {
        const was = want[0][0] + ',' + want[0][1] + ',' + want[0][2]
          + '|' + want[1][0] + ',' + want[1][1] + ',' + want[1][2];
        want[0] = hslToWash(a);
        want[1] = hslToWash(b);
        const isNow = want[0][0] + ',' + want[0][1] + ',' + want[0][2]
          + '|' + want[1][0] + ',' + want[1][1] + ',' + want[1][2];
        if (isStill()) { now[0] = want[0].slice(); now[1] = want[1].slice(); frame(); return; }
        /* A COLOUR ARRIVES AS SOMETHING POURED IN, not as a crossfade that
           happens to the whole room at once. The tint still walks over about
           a second and a half; the ring is what tells you it started, and it
           starts where the last thing was touched, so choosing a note reads
           as that note going into the water. Only on a real change - the
           observer fires on every attribute write and most of them say the
           same thing twice. */
        if (was !== isNow) this.drop(pxN, pyN);
      },
      mood(m) {
        aim.scale = m.scale; aim.warp = m.warp; aim.speed = m.speed; aim.amp = m.amp;
        if (isStill()) {
          dial.scale = m.scale; dial.warp = m.warp; dial.speed = m.speed;
          dial.amp = m.amp * ampAim;
          frame();
        }
      },
      /* the liquid leans toward the pointer, about a tenth of the field and
         over three seconds, so it is felt rather than followed. One number
         a frame; there is no listener cost beyond the assignment. */
      point(x, y) { pxN = x; pyN = y; },
      /* a drop. Refused under reduced motion, which is the whole point of
         reduced motion, and capped at RIP_MAX so a shopper drumming their
         fingers on the room cannot make it expensive. */
      drop(x, y) {
        if (dead || isStill() || !cv) return;
        if (holdT) { if (held.length < 2) held.push([x, y]); return; }
        /* ONE TAP, ONE RING. A press drops a ring under the finger and the
           colour it changes drops another from the same point a moment
           later; two rings born together at one spot are one ring drawn
           twice as dark, at twice the cost. */
        const last = rings[rings.length - 1];
        if (last && clock - last.t0 < 0.3 && Math.abs(last.x - x) + Math.abs(last.y - y) < 0.12) return;
        if (rings.length >= RIP_MAX) rings.shift();
        rings.push({ x: x, y: y, t0: clock, front: 0, fade: 1 });
        run();
      },
      hold(ms) {
        if (dead || isStill() || !cv) return;
        stop();
        window.clearTimeout(holdT);
        holdT = window.setTimeout(() => {
          holdT = 0;
          if (dead) return;
          held.splice(0).forEach((d) => this.drop(d[0], d[1]));
          run();
        }, ms);
      },
      wake() { forget(); run(); },
      rest() { stop(); },
      gone() {
        stop(); forget();
        window.clearTimeout(holdT); holdT = 0; held.length = 0;
        if (ro) ro.disconnect();
        if (cv && cv.parentElement) cv.parentElement.removeChild(cv);
        cv = null; g = null; host = null; img = null; px8 = null;
        rings.length = 0;
      }
    };
  }
  const field = makeField();
  /* the longest panel slide in either sheet is the Duet's 620ms transform */
  const STEP_HOLD_MS = 640;
  function fieldPanel() { return root.querySelector('.sf-panel.is-on, .dt-panel.is-on'); }

  /* ============================================================
     THE BED, GIVEN TO THE FINDER (2026-08-31, owner: "on empty space right
     we needs something magical to happen on desktop" and "the design and
     language needs to be re-used between both systems").

     The Duet's quiz asks its questions standing in the range - three faint
     cut-outs drifting behind the copy - and the finder, which asks five
     questions on the same shell, asked all five on a blank page. On a spread
     that is a whole empty column: at 1440 the finder's question panels put
     the copy in the left page and the controls in the right, and the left
     page is copy and nothing else from the top of the room to the bottom.

     So the finder gets the same bed, built from the bottles its OWN cover is
     already showing - `.sf-hero` is server-rendered on the first panel, so
     there is nothing to fetch and nothing to choose. One bed element is made
     per question panel, once, the first time the room walks onto it.

     CUT-OUTS ONLY, and it is not a preference. A bed image is drawn at a
     tenth of its opacity, which makes it its own stacking context, and a
     stacking context around a BEDDED packshot prints its #F2F2F2 plate as a
     rectangle (L3, steering/15 §9). A transparent PNG has no plate to print.
     quizBed() in theme.js refuses a bedded plate for exactly this reason and
     so does this: a cover bottle with no cut-out is simply skipped, and a
     room with no cut-outs at all gets no bed rather than three pale boxes.
     ============================================================ */
  function bedFor(panel) {
    if (!isFinder || !panel || panel.querySelector('.lq-bed')) return;
    /* the cover has its own fan and the ANSWER has its own photograph; the
       bed belongs to the five questions, which are the panels with a page to
       spare */
    if (panel.classList.contains('sf-panel--cover')) return;
    if (panel.classList.contains('sf-panel--out')) return;
    const src = [...root.querySelectorAll('.sf-hero img')]
      .map((im) => im.currentSrc || im.src)
      .filter((u) => u && u.indexOf('-cut.') !== -1)
      .slice(0, 3);
    if (!src.length) return;
    /* DECODED BEFORE THEY ARE SHOWN (2026-09-22, lane 8). The three are the
       cover's own cut-outs, already in the cache, but an image put into the
       page undecoded is decoded in the frame that first paints it, which is
       the frame its rise animation starts in. decode() does that work off
       the main thread first; the bed goes in when all three are ready, or
       as soon as any of them fails. */
    const imgs = src.map((u) => {
      const im = new Image();
      im.alt = '';
      im.decoding = 'async';
      im.src = u;
      return im;
    });
    const ready = imgs.map((im) => (im.decode ? im.decode().catch(() => null) : null));
    Promise.all(ready).then(() => {
      if (dead || !panel.isConnected || panel.querySelector('.lq-bed')) return;
      const bed = document.createElement('div');
      bed.className = 'lq-bed';
      bed.setAttribute('aria-hidden', 'true');
      imgs.forEach((im) => bed.appendChild(im));
      const scroll = panel.querySelector('.sf-panel__scroll');
      /* inside the scroller, before the copy: the bed belongs to the QUESTION,
         so it scrolls with it rather than sitting over the whole room. Same
         correction the Duet's own bed took on 2026-08-28. */
      if (scroll) scroll.insertBefore(bed, scroll.firstChild);
      else panel.insertBefore(bed, panel.firstChild);
    });
  }

  /* ---- teardown: the flat room is v1's, whole ---- */
  function teardown() {
    if (dead) return;
    dead = true;
    try { mo.disconnect(); } catch (err) { /* already gone */ }
    try { field.gone(); } catch (err) { /* never mounted */ }
    root.classList.remove('lq-live');
    root.classList.remove('lq-field-on');
    root.style.removeProperty('--lq-tint');
    root.style.removeProperty('--lq-tint-b');
  }

  /* ---- observe. Both controllers write data-at on every step and
     repaint data-chat-context on every change, so one observer hears
     everything without a single hook inside them. ---- */
  const mo = new MutationObserver((muts) => {
    if (dead) return;
    applyState();
    for (const m of muts) {
      if (m.attributeName === 'data-at') {
        paintOptionTints();
        const on = fieldPanel();
        /* the field holds still for the slide (THE HOLD, above) and the bed
           is laid once the panel has landed, so the step's own frame carries
           neither a repaint of the water nor three new pictures */
        field.hold(STEP_HOLD_MS);
        field.to(on);
        window.setTimeout(() => { if (!dead && fieldPanel() === on) bedFor(on); }, STEP_HOLD_MS);
        /* and the room pours into the chapter it just walked into. Slightly
           above centre because that is where the eye already is - the
           question's own first line - so the ring opens outward from the
           words rather than from the shopper's feet. Held, so it is poured
           as the panel lands. */
        field.drop(0.5, 0.42);
        break;
      }
    }
  });
  mo.observe(root, { attributes: true, attributeFilter: ['data-at', 'data-chat-context', 'class'] });

  /* ---- the liquid leans toward the pointer. Passive, coalesced to one
     assignment (the field walks toward it on its own clock), and never
     bound on a touch screen, where there is no pointer to follow and the
     listener would only fire on taps. ---- */
  if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    root.addEventListener('pointermove', (e) => {
      if (dead) return;
      const b = root.getBoundingClientRect();
      if (b.width < 2 || b.height < 2) return;
      field.point((e.clientX - b.left) / b.width, (e.clientY - b.top) / b.height);
    }, { passive: true });
  }

  /* ---- EVERY TAP IS A DROP. The ring is put where the finger or the
     pointer actually landed, in the coordinates of the PANEL the canvas is
     mounted in rather than of the room, because those are the two boxes the
     field works in and on a spread they are not the same box.

     pointerdown, not click: the ring should already be moving while the
     press is happening, which is what makes it feel like the surface
     responded rather than the page did. Passive and capturing, so it never
     delays or blocks the control being pressed underneath it - the room's
     own handlers are untouched and never see this listener at all. ---- */
  root.addEventListener('pointerdown', (e) => {
    if (dead) return;
    const on = fieldPanel();
    if (!on) return;
    const b = on.getBoundingClientRect();
    if (b.width < 2 || b.height < 2) return;
    const nx = (e.clientX - b.left) / b.width;
    const ny = (e.clientY - b.top) / b.height;
    /* the last touch is also where a colour change will ring from, which is
       the only way a phone ever sets this: there is no pointermove to follow
       on a touch screen, so without this line every note a thumb chooses
       would pour into the middle of the room instead of under the thumb. */
    field.point(nx, ny);
    field.drop(nx, ny);
  }, { passive: true, capture: true });

  /* the room stops paying for the field the moment nobody is looking at it */
  document.addEventListener('visibilitychange', () => {
    if (dead) return;
    if (document.hidden) field.rest(); else field.wake();
  });
  if (still && still.addEventListener) {
    still.addEventListener('change', () => {
      if (dead) return;
      /* reduced motion switched on mid-session: the loop stops before the
         field is re-pointed at its panel, which then draws the still frame */
      if (isStill()) field.rest();
      field.to(fieldPanel());
    });
  }

  applyState();
  paintOptionTints();
  /* the panel a room opens on is already `is-on` when this runs; a room that
     has not painted one yet takes the field on its first `data-at` */
  root.classList.add('lq-field-on');
  bedFor(fieldPanel());
  field.to(fieldPanel());
})();
